import {
    IComplexFilter,
    isComplexFilterArr,
    TValueHelperValue
} from "@components/conditionalFilterDialog/ConditionalFilterDialog.utils";
import { AttachmentIndicatorIcon } from "@components/icon";
import { isValidDate } from "@components/inputs/date/utils";
import { GroupDividerSize, ISelectItem } from "@components/inputs/select/Select.types";
import { getRouteByDocumentType, getRouteByEntityType } from "@odata/EntityTypes";
import { IFieldInfo } from "@odata/FieldInfo.utils";
import {
    AddressEntity,
    CountryEntity,
    DocumentEntity,
    EntitySetName,
    EntityTypeName,
    GrantBalanceEntity,
    GrantEntity,
    IAddressEntity,
    ICompanyBankAccountEntity,
    ICompanyLegalAddressEntity,
    IDocumentEntity,
    IEntityLabelEntity,
    IGrantEntity,
    ILabelHierarchyEntity,
    IPaymentDocumentItemEntity,
    IVatEntity,
    VatEntity
} from "@odata/GeneratedEntityTypes";
import { CountryCode, DocumentTypeCode, GrantStatusCode, VatCode, VatReverseChargeCode } from "@odata/GeneratedEnums";
import {
    createFilterValue,
    ICreateFilterStringSettings,
    IFormatOptions,
    transformToODataString
} from "@odata/OData.utils";
import { isEeaRule, isEeaRuleExceptAgendaDomestic } from "@pages/admin/vatRules/VatRules.global.utils";
import { isVatRegisteredEntity } from "@pages/admin/vatRules/VatRules.utils";
import { TEntityWithAccount } from "@pages/banks/bankAccounts/BankAccounts.utils";
import {
    getAccountUsageAcrossCharts,
    IAccountUsage,
    isAccountUsedCheck
} from "@pages/chartOfAccounts/ChartOfAccounts.utils";
import { getAgendaCountryCode, getCompanyUsedCurrencyCodes } from "@pages/companies/Company.shared.utils";
import { getVatItemsDecisiveDateName, isReceivedBc } from "@pages/documents/Document.utils";
import { IDocumentExtendedEntity } from "@pages/documents/DocumentInterfaces";
import { isOssDocument } from "@pages/documents/Oss.global.utils";
import { IFilesDefinition, TFieldDefinition, TFieldsDefinition } from "@pages/PageUtils";
import { IAddressExtendedEntity } from "@utils/address.utils";
import { getCompanyCurrency, isVatRegisteredCompany } from "@utils/CompanyUtils";
import { isDefined, isNotDefined, isObjectEmpty } from "@utils/general";
import { removeWhiteSpace } from "@utils/string";
import i18next from "i18next";
import React from "react";
import { ValidationError } from "yup";

import { EMPTY_VALUE } from "../../constants";
import {
    BankAccountType,
    BasicInputSizes,
    CacheStrategy,
    FastEntryInputSizes,
    FieldType,
    GroupedField,
    IconSize,
    LabelStatus,
    NavigationSource,
    PaneStatus,
    QueryParam,
    TextAlign,
    ValidatorType,
    ValueType
} from "../../enums";
import { TRecordAny, TRecordType, TValue } from "../../global.types";
import { Model } from "../../model/Model";
import { IFilterQuery, TableStorage } from "../../model/TableStorage";
import { PHONE_REG, POSTAL_CODE_REG } from "../../model/Validator.types";
import BindingContext, { createPath, IEntity } from "../../odata/BindingContext";
import { getQueryParameters, setQueryParams } from "../../routes/Routes.utils";
import { getUtcDateBy, getUtcDayjs } from "../../types/Date";
import NumberType from "../../types/Number";
import memoize from "../../utils/memoize";
import { FormStorage, IFormStorageDefaultCustomData } from "../../views/formView/FormStorage";
import { IChangedFilter } from "../../views/table/TableView.utils";
import Clickable from "../clickable";
import { getIntentLink, getIntentNavParams } from "../drillDown/DrillDown.utils";
import { IFormatterFns } from "../inputs/input/WithFormatter";
import { ICellValueObject, TCellValue, TId } from "../table";
import Text from "../text";
import { tableTokenFormatter } from "../token";
import {
    getInfoValue,
    getSimpleBoolSelectItems,
    IFieldDef,
    IFieldInfoProperties,
    IGetValueArgs,
    isFieldReadOnly,
    not,
    TValidatorFnResult
} from "./FieldInfo";
import { getCollapsedGroupId } from "./Smart.utils";
import { IFilterDef, TFilterDef } from "./smartFilterBar/SmartFilterBar.types";
import { TFormatterFn } from "./smartTable/SmartTable.utils";

// Basic definition for field, which is not visible in the form, but should store & update its value,
// which is set programmatically (e.g. before save or just as default value)
export const BackgroundFieldDef: IFieldInfoProperties = {
    isVisible: false,
    clearIfInvisible: false,
    customizationData: {
        useForCustomization: false
    }
};

export const AccountDefFormatter = (val: TValue, args?: IFormatOptions): string => {
    const entity = args.item ?? {};
    return entity.Number ? `${entity.Number ?? ""} - ${entity.Name ?? ""}` : entity.Name;
};

export const getAccountDef = (path?: string): IFieldInfoProperties => {
    const obj: IFieldInfoProperties = {
        type: FieldType.HierarchyComboBox,
        cacheStrategy: CacheStrategy.View,
        fieldSettings: {
            displayName: "Number",
            localDependentFields: [
                {
                    from: { id: "Number" }, to: { id: "Number" },
                    navigateFrom: NavigationSource.Itself
                },
                {
                    from: { id: "Name" }, to: { id: "Name" },
                    navigateFrom: NavigationSource.Itself
                }
            ]
        },
        columns: [{
            id: "Number",
            additionalProperties: [{ id: "/Name" }, { id: "/IsClosed" }],
            formatter: AccountDefFormatter
        }],
        additionalProperties: [{ id: "Name" }],
        formatter: AccountDefFormatter
    };

    if (path) {
        obj.additionalProperties.push({
            id: path
        });
    }

    return obj;
};

export const CurrencyDef: IFieldInfoProperties = {
    type: FieldType.ComboBox,
    cacheStrategy: CacheStrategy.Route,
    fieldSettings: {
        displayName: "Code",
        shouldDisplayAdditionalColumns: true,
        entitySet: EntitySetName.Currencies
    },
    filter: {
        select: ({ context }) => {
            const usedByCompany = getCompanyUsedCurrencyCodes(context);
            return `Code in (${usedByCompany.map((code: string) => `'${code}'`).join(", ")})`;
        }
    },
    defaultValue: (args: IGetValueArgs) => {
        return getCompanyCurrency(args.context ?? args.storage.context);
    },
    columns: [{ id: "Code" }, { id: "Name" }],
    additionalProperties: [{
        id: "MinorUnit"
    }, {
        id: "Name"
    }]
};

export const SingleBusinessPartnerDef: IFieldInfoProperties = {
    type: FieldType.BusinessPartnerSelect,
    fieldSettings: {
        allowCreate: true,
        groups: [{
            id: "aresCZ",
            title: "ARES",
            dividerSize: GroupDividerSize.Small
        }, {
            id: "aresSK",
            title: "ARES.SK",
            dividerSize: GroupDividerSize.Small
        }]
    }
};

export const getLabelsDef = (translationFile: string): IFieldInfoProperties => ({
    label: i18next.t(`${translationFile}:Form.Labels`),
    type: FieldType.LabelSelect,
    width: BasicInputSizes.XL,
    defaultValue: [],
    fieldSettings: {
        keyPath: "Label/Id",
        localDependentFields: [{
            from: { id: "LabelHierarchy/Id" },
            to: { id: "LabelHierarchy/Id" }
        }]
    },
    additionalProperties: [{ id: "Label" }, { id: "LabelHierarchy" }],
    getBulkPayloadValue: (value) => (value as IEntity[])?.map(item => ({
        LabelHierarchyId: item.LabelHierarchy?.Id,
        LabelId: item.Label?.Id
    }))
});

export const getGrantDef = (): IFieldInfoProperties => ({
    label: i18next.t("Grants:Form.Grant"),
    type: FieldType.ComboBox,
    width: BasicInputSizes.XL,
    additionalProperties: [
        { id: "NumberOurs" },
        { id: "ShortName" }
    ],

    defaultValue: null,
    fieldSettings: {
        itemsFactory: async (args) => {
            const result = await args.storage.oData
                .fromPath(EntitySetName.Grants)
                .query()
                .select(GrantEntity.Id, GrantEntity.NumberOurs, GrantEntity.ShortName)
                .expand(GrantEntity.Balance, e => e.select(GrantBalanceEntity.StatusCode))
                .filter(`${createPath(GrantEntity.Balance, GrantBalanceEntity.StatusCode)} in ('${GrantStatusCode.Applied}','${GrantStatusCode.InProgress}')`)
                .top(50)
                .fetchData<Partial<IGrantEntity>[]>();

            return result.value.map(g => ({
                id: g.Id,
                label: `${g.NumberOurs} – ${g.ShortName}`,
                additionalData: g
            }));
        },

    }
});

export async function extractVirtualColumnsDef(def: IFieldDef, storage: Model): Promise<IEntity[]> {
    const columnBc = storage.data.bindingContext.navigate(def.id);
    const entitySet = columnBc.getEntitySet().getName();
    const keyPropertyName = columnBc.getKeyPropertyName();
    const { displayName } = def.fieldSettings;
    const query = storage.oData.getEntitySetWrapper(entitySet as EntitySetName)
        .query()
        .filter(getInfoValue(def.filter, "select", { context: storage.context }))
        .select(displayName, keyPropertyName)
        .orderBy(displayName);
    const result = await query.fetchData<IEntity[]>();

    return result?.value;
}

export const extractVirtualColumnsDefMemoized = memoize(extractVirtualColumnsDef,
    (def: IFieldDef): string => {
        return def.id; // todo: better resolver...
    });

const LabelsTableColumnDefinitionId = "Labels/LabelHierarchy";
export const LabelsTableColumnDef: TFieldsDefinition = {
    [LabelsTableColumnDefinitionId]: {
        factory: async (storage): Promise<IFieldDef[]> => {
            const labelHierarchies: ILabelHierarchyEntity[] = await extractVirtualColumnsDefMemoized({
                id: LabelsTableColumnDefinitionId,
                fieldSettings: { displayName: "Name" }
            }, storage);

            const tableColumnDef = {
                fieldSettings: {
                    disableSort: true,
                    displayName: "Name"
                },
                textAlign: TextAlign.Left,
                additionalProperties: [{ id: "/Labels/Label/Name" }, { id: "Color" }],
                formatter: (val: TValue, args: IFormatOptions): TCellValue => {
                    const { entity, info } = args;
                    const { key } = BindingContext.parseKey(info.id, true);

                    const item = entity.Labels.find((documentLabel: IEntityLabelEntity) => documentLabel.LabelHierarchy?.Id === key);

                    return tableTokenFormatter(item?.Label?.Name, item?.LabelHierarchy?.Color);
                }
            };
            const columnBc = storage.data.bindingContext.navigate(LabelsTableColumnDefinitionId);

            return labelHierarchies.map(hierarchy => {
                const columnId = columnBc.addKey(hierarchy).getNavigationPath();
                return {
                    ...tableColumnDef,
                    id: columnId,
                    label: hierarchy.Name
                };
            });
        }
    }
};

/**
 * Returns filter definition
 * @param hasDocumentItems
 */
export const getLabelsFilterDef = (hasDocumentItems = false): TRecordType<TFilterDef> => {
    const basePath = "Labels";
    const filterProp = "Label";
    const filterPath = createPath(basePath, filterProp);

    return {
        [filterPath]: {
            factory: async (storage, filterId): Promise<(IFilterDef & IFieldInfo)[]> => {
                let labelHierarchies: ILabelHierarchyEntity[] = await extractVirtualColumnsDefMemoized({
                    id: `${basePath}/LabelHierarchy`,
                    fieldSettings: {
                        displayName: "Name"
                    }
                }, storage);

                let hierarchyId: number;
                const columnBc = storage.data.bindingContext.navigate(basePath);

                if (filterId) {
                    // filterId is specified -> return only the filter, which corresponds with the Id
                    const result = BindingContext.parseKey(filterId, true);
                    hierarchyId = result?.key as number;
                    labelHierarchies = labelHierarchies.filter(hierarchy => hierarchy.Id === hierarchyId);
                }

                return labelHierarchies
                    .map((hierarchy) => {
                        const bindingContext = columnBc.addKey(hierarchy).navigate(filterProp);
                        const id = bindingContext.getNavigationPath(false);
                        return {
                            id,
                            bindingContext,
                            type: FieldType.LabelSelect,
                            label: hierarchy.Name,
                            isValueHelp: false,
                            textAlign: TextAlign.Left,
                            additionalProperties: [{ id: "/Labels/Label/Name" }, { id: "Color" }],
                            fieldSettings: {
                                entitySet: `LabelHierarchies(${hierarchy.Id})/Labels`,
                                displayName: "Name",
                                // todo this in combination with filterName helps to show correct label in read only filter bar
                                // but sometimes when you add/remove label hierarchy from filters, filter query is not built correctly
                                idName: "Name",
                                dontSetCurrentValueFromEntity: true,
                                oneItemPerHierarchy: false
                            },
                            // generated filter flag -> uses keys in path
                            factory: true,
                            shouldNotFilterCollection: true,
                            filter: {
                                select: hierarchyId ? `Id eq ${hierarchyId}` : "",
                                buildFilter(item: IChangedFilter, settings: ICreateFilterStringSettings, storage: TableStorage): string | IFilterQuery {
                                    let arrayValues: TValueHelperValue[];
                                    if (Array.isArray(item.value)) {
                                        // value helper values -> list of string
                                        arrayValues = [...item.value] as unknown as TValueHelperValue[];
                                    } else {
                                        // there is no such option for labels filter
                                        return null;
                                    }
                                    // filter out empty value as it's kind of special...
                                    const values = arrayValues?.filter(v => v !== EMPTY_VALUE);
                                    const hasEmptyValue = values.length !== arrayValues.length;

                                    const _getFilterQuery = (prefix: string, vals: TValueHelperValue[] = null): string => {
                                        let filterValue = "";
                                        if (vals?.length) {
                                            if (isComplexFilterArr(vals)) {
                                                filterValue = vals.map((v: IComplexFilter) => createFilterValue(bindingContext, "l/Label/Name", v)).join(" and ");
                                            } else {
                                                filterValue = `l/Label/Name in (${transformToODataString(vals, ValueType.String)})`;
                                            }
                                        }
                                        return `${prefix}/any(l: l/LabelHierarchy/Id eq ${hierarchyId}${filterValue ? ` and ${filterValue}` : ""})`;
                                    };

                                    const _getLabelFilters = (vals: TValueHelperValue[] = null) => {
                                        const ret: string[] = [_getFilterQuery(basePath, vals)];
                                        if (hasDocumentItems) {
                                            // filter also document items
                                            ret.push(`Items/any(i: ${_getFilterQuery("i/LabelSelection/Labels", vals)})`);
                                        }
                                        return ret;
                                    };

                                    // generate standard label filters
                                    const filters: string[] = [];
                                    if (values.length) {
                                        filters.push(..._getLabelFilters(values));
                                    }

                                    if (hasEmptyValue) {
                                        const emptyFilters = _getLabelFilters();
                                        filters.push(`not(${emptyFilters.join(" or ")})`);
                                    }

                                    return filters?.length > 1 ? `(${filters.join(" or ")})` : filters[0];
                                }
                            }
                        };
                    });
            }
        }
    };
};


export const vatEntityRateFormatter = (vat: TValue | IVatEntity): string => {
    const vatEntity = vat as IVatEntity;
    return vatRateFormatter(typeof (vat) === "number" ? vat : vatEntity?.Rate) as string;
};

export const vatRateFormatter: TFormatterFn = function (value) {
    return isDefined(value) ? `${value} %` : i18next.t("Common:Select.NoVat") as string;
};

export function getValidVats(storage: FormStorage, docType?: DocumentTypeCode, date?: Date): IVatEntity[] {
    const allVats = (storage.context.getData().custom?.Vats ?? []) as IVatEntity[];
    const document = storage.data.entity as IDocumentExtendedEntity;
    const countryCode = isOssDocument(storage, date) ? document?.BusinessPartner?.Country?.Code ?? CountryCode.CzechRepublic : getAgendaCountryCode(storage.context);

    // https://solitea-cz.atlassian.net/browse/DEV-26594
    const docTypesWithAlmostAllVats = [DocumentTypeCode.CorrectiveInvoiceReceived, DocumentTypeCode.CorrectiveInvoiceIssued];

    // docType is not set for DocumentItemTemplate entity, which shows common Vats -> use currentDate in this case
    const currentDate = date ?? (docType && storage.getValueByPath(getVatItemsDecisiveDateName(storage))) ?? getUtcDayjs();
    let fromDate = currentDate;

    if (!docType || docTypesWithAlmostAllVats.includes(docType)) {
        // we need to display valid vats in last 3 years
        fromDate = getUtcDayjs(currentDate).subtract(3, "years").startOf("year");
    }

    return allVats.filter((vat) => vat.CountryCode === countryCode
        && (!vat.DateValidFrom || getUtcDayjs(vat.DateValidFrom).isSameOrBefore(currentDate, "day"))
        && (!vat.DateValidTo || getUtcDayjs(vat.DateValidTo).isSameOrAfter(fromDate, "day"))
    );
}

// if we access i18next, we have to use callback fn instead of static variable
export const getDocumentItemVatDef: (docType?: DocumentTypeCode) => Omit<IFieldDef, "id"> = (docType: DocumentTypeCode) => {
    return {
        type: FieldType.ComboBox,
        cacheStrategy: CacheStrategy.View,
        fieldSettings: {
            displayName: VatEntity.Rate,
            itemsForRender: (origItems: ISelectItem[], args: IGetValueArgs): ISelectItem[] => {
                const correspondingVats = getValidVats(args.storage as FormStorage, docType);

                const items: ISelectItem[] = correspondingVats.map((vat: IVatEntity): ISelectItem => {
                    const label = vatEntityRateFormatter(vat) as string;

                    return {
                        label: label,
                        id: vat.Code,
                        // since we're building the item by our selves,
                        // we need to add additionalProperties as well, otherwise Rate won't get propagated properly
                        additionalData: {
                            Code: vat.Code,
                            Rate: vat.Rate,
                            DateValidFrom: vat.DateValidFrom,
                            DateValidTo: vat.DateValidTo
                        }
                    };
                });

                items.unshift({
                    id: null,
                    label: i18next.t("Common:Select.NoVat")
                });

                return items;
            }
        },
        formatter: (val, args) => {
            const value = args.item?.Rate ?? val;
            return vatEntityRateFormatter(value ?? 0);
        },
        additionalProperties: [{
            id: "Rate"
        }],
        defaultValue: (args) => {
            const isReceived = isReceivedBc(args.storage.data.bindingContext);
            const document = args.storage.data.entity as IDocumentEntity;
            const vatRule = document.VatClassificationSelection?.VatClassification;
            const _isEeaRuleExceptAgendaDomestic = isEeaRuleExceptAgendaDomestic(args);
            const _isVatRegistered = isVatRegisteredEntity(args);

            if (isReceived && !_isVatRegistered && isEeaRule(args)) {
                return null; // Received document from non-payer has no Vat
            }
            if (isReceived && _isEeaRuleExceptAgendaDomestic && !vatRule?.IsInAutomatedVatInclusionMode) {
                return null; // DEV-13419 - received documents in automatedVatInclusionMode
            }
            if (!isReceived && _isEeaRuleExceptAgendaDomestic && vatRule && vatRule.VatReverseChargeCode !== VatReverseChargeCode.Ne) {
                return null; // DEV-13415 - issued documents in reverseCharge
            }

            if (!isVatRegisteredCompany(args.storage.context)) {
                return null; // Company is not VAT registered
            }

            if (!isOssDocument(args.storage as FormStorage) || (
                !document?.BusinessPartner?.Country?.Code || document.BusinessPartner.Country.Code === CountryCode.CzechRepublic
            )) {
                return {
                    Code: VatCode.CzStd2013,
                    // TODO it would be better to retrieve additionalProperties for defaultValue from backend
                    Rate: 21
                };
            }

            // we don't know the correct default rate for other countries...
            return null;
        },
        width: FastEntryInputSizes.XS,
        // TODO this caused problems in some cases (like https://solitea-cz.atlassian.net/browse/DEV-34674)
        // can be uncommented, once we have a better understanding of what VAT items are supposed to be shown
        // in what cases
        // TODO if uncommented, DocumentFormView.validateVatFields could probably be removed?
        // validator: {
        //     type: ValidatorType.Custom,
        //     settings: {
        //         customValidator: (value: TValue, args: IGetValueArgs): TValidatorFnResult => {
        //             if (!value) {
        //                 // 0% vat is valid
        //                 return true;
        //             }
        //
        //             const correspondingVats = getValidVats(args.storage as FormStorage, docType);
        //             const vatFound = correspondingVats.find((vat: IVatEntity) => vat.Code === value);
        //
        //             if (!vatFound) {
        //                 return new ValidationError(args.storage.t("Document:Form.WrongVat"), false, args.bindingContext.getNavigationPath(false));
        //             }
        //
        //             return true;
        //         }
        //     }
        // }
    };
};

export const countryDef: IFieldInfoProperties = {
    type: FieldType.ComboBox,
    cacheStrategy: CacheStrategy.EndOfTime,
    defaultValue: (args: IGetValueArgs) => {
        const context = args?.context || args?.storage?.context;
        const currentCompany = context?.getCompany();
        return currentCompany?.Accounting?.CountryCode ?? CountryCode.CzechRepublic;
    },
    columns: [
        {
            id: "Name",
            additionalProperties: [
                { id: CountryEntity.IsEuMember },
                { id: CountryEntity.IsEEA }
            ]
        },
        { id: "Code" }
    ],
    fieldSettings: {
        displayName: "Name",
        shouldDisplayAdditionalColumns: true,
        preloadItems: true,
        localDependentFields: [{
            from: { id: CountryEntity.IsEEA },
            to: { id: CountryEntity.IsEEA },
            navigateFrom: NavigationSource.Itself
        }, {
            from: { id: CountryEntity.IsEuMember },
            to: { id: CountryEntity.IsEuMember },
            navigateFrom: NavigationSource.Itself
        }]
    }
};

interface IGetAddressOpts {
    addHouseStreetNumber?: boolean; // whether to add separate HouseNumber field or use StreetNumber field instead (for better compatibility with existing backends)
    withoutCountry?: boolean;
    multiline?: boolean; // if address should be split to 2 lines (group)
}

export const splitAddressNumber = (jointNumbers: string): Pick<ICompanyLegalAddressEntity, "HouseNumber" | "StreetNumber"> => {
    const [HouseNumber, StreetNumber] = jointNumbers?.split("/", 2) ?? [jointNumbers || ""];
    return {
        HouseNumber,
        StreetNumber: StreetNumber ?? ""
    };
};

export const joinAddressToStreetString = (a: ICompanyLegalAddressEntity): string => {
    const number = [a?.HouseNumber, a?.StreetNumber].filter(Boolean).join("/");
    return [a?.Street || a?.City, number].filter(Boolean).join(" ");
};

export const getAddressFields = (name: string, args: Partial<IFieldInfoProperties> = {}, opts?: IGetAddressOpts): Record<string, IFieldInfoProperties> => {
    const path = name !== "" ? `${name}/` : "";
    return {
        [`${path}Street`]: {
            groupedField: opts?.addHouseStreetNumber ? GroupedField.WrappedMultiStart : undefined,
            width: BasicInputSizes.L,
            ...args
        },
        ...(opts?.addHouseStreetNumber ? {
            [`${path}HouseNumber`]: {
                width: BasicInputSizes.S,
                ...args
            },
            [`${path}StreetNumber`]: {
                width: BasicInputSizes.S,
                groupedField: GroupedField.WrappedMultiEnd,
                ...args,
                isRequired: false // StreetNumber is never required as some addresses don't have it.
            },
        } : {}),
        [`${path}City`]: {
            groupedField: GroupedField.MultiStart,
            width: BasicInputSizes.M,
            ...args
        },
        [`${path}PostalCode`]: {
            ...getPostalCodeDef(opts?.withoutCountry),
            groupedField: GroupedField.MultiEnd,
            ...args
        },
        ...(!opts?.withoutCountry ? {
            [`${path}Country`]: {
            ...countryDef,
            affectedFields: [
                { id: "PostalCode", navigateFromParent: true, revalidate: true }
            ],
            ...args
        }} : {})
    };
};

const ADDRESS_FIELDS = [AddressEntity.Street, AddressEntity.City, AddressEntity.PostalCode, AddressEntity.Country];
const FULL_ADDRESS_FIELDS = [AddressEntity.Street, "HouseNumber", "StreetNumber", AddressEntity.City, AddressEntity.PostalCode, AddressEntity.Country];
// todo: make it getter with opts to add House and StreetNumber if needed
export const addressFieldsToQuery = ADDRESS_FIELDS.map(item => item === AddressEntity.Country ? AddressEntity.CountryCode : item);

export const getAddressGroup = (prefix: string, opts?: IGetAddressOpts): IFieldDef[][] => {
    let rows = [[
        "Street", "HouseNumber", "StreetNumber"
    ], [
        "City", "PostalCode"
    ]];

    if (!opts?.withoutCountry) {
        rows[1].push("Country");
    }

    if (!opts?.addHouseStreetNumber) {
        rows = rows.map(row => row.filter(field => field !== "HouseNumber" && field !== "StreetNumber"));
    }

    if (!opts?.multiline) {
        // flatten to one row
        rows = [rows.flat()];
    }

    return rows.map(row => row.map(field => ({ id: prefix ? `${prefix}/${field}` : field })));
};

export function isAddressEmpty(address: IAddressExtendedEntity): boolean {
    return isObjectEmpty(address) || FULL_ADDRESS_FIELDS
        .filter(item => item !== AddressEntity.Country)
        .every(field => isNotDefined(address[field as keyof IAddressExtendedEntity]));
}

function getNumberValidator(length: number) {
    return {
        type: ValidatorType.String,
        settings: {
            numberString: true,
            max: length
        }
    };
}

export const SymbolConstantDef: IFieldInfoProperties = {
    fieldSettings: {
        parser: (val: string) => {
            const value = removeWhiteSpace(val);
            // strip / pad with leading zeros (if it contains just numbers) as it's the only valid case
            return /^[0-9]+$/.test(value) ? parseInt(value).toString().padStart(4, "0") : value;
        }
    },
    validator: getNumberValidator(10)
};

export const SymbolVariableDef: IFieldInfoProperties = {
    validator: getNumberValidator(10),
    fieldSettings: {
        parser: (val: string) => {
            return val?.trim();
        }
    }
};

export const SymbolSpecificDef: IFieldInfoProperties = {
    validator: getNumberValidator(10),
    fieldSettings: {
        parser: (val: string) => {
            return val?.trim();
        }
    }
};

export const getSavedAccountsGroups = (): IFieldDef[] => {
    return [
        { id: BankAccountType.Account },
        { id: BankAccountType.ABA },
        { id: BankAccountType.IBANOnly },
        { id: BankAccountType.Service }
    ];
};

export const getSavedAccountsDef = (): IFieldInfoProperties => {
    return {
        type: FieldType.ComboBox,
        fieldSettings: {
            items: [],
            groups: getSavedAccountsGroups()
        },
        comparisonFunction: (entity: IDocumentEntity, origEntity: IDocumentEntity) => {
            return origEntity.BankAccount?.BankCode === entity.BankAccount?.BankCode &&
                origEntity.BankAccount?.AccountNumber === entity.BankAccount?.AccountNumber;
        }
    };
};


// Todo: we may default the prefix / enhance formatting according to country
// Todo: consider DB format as +420.123456789, so we can distinguish country prefix
export const phoneNumberInputFormatter: IFormatterFns<string>["formatter"] = (value: string): string => {
    if (!value || !PHONE_REG.test(value)) {
        return value;
    }

    const match = value.match(/^(\+(42[01]|[0-9]{1,2}))?([0-9]+)$/);
    const countryCode = match[1];
    const number = match[3];
    const parts: string[] = number.match(/.{1,3}/g) || [];
    parts.unshift(countryCode ?? "+420");
    return parts.join(" ");
};

export const phoneNumberFormatter: TFormatterFn = (val: TValue, args?: IFormatOptions) => {
    return phoneNumberInputFormatter(val as string);
};

export const phoneNumberParser: IFormatterFns<string>["parser"] = (value: string) => {
    // replace leading zeroes to +
    value = value.replace(/^00/, "+");
    // we just remove whitespaces and dash characters, other characters
    // should correct user manually if present @see Validator.ts -> PHONE_REG
    return value.replaceAll(/[-\s]+/g, "");
};

export const phoneNumberIsValid: IFormatterFns<string>["isValid"] = (value: string): boolean => {
    return PHONE_REG.test(value);
};

export const PhoneNumberDef: IFieldInfoProperties = {
    validator: {
        type: ValidatorType.Phone
    },
    fieldSettings: {
        formatter: phoneNumberInputFormatter,
        parser: phoneNumberParser,
        isValid: phoneNumberIsValid
    }
};

export const EmailDef: IFieldInfoProperties = {
    width: BasicInputSizes.L,
    validator: {
        type: ValidatorType.Email
    },
};

export const postalCodeFormatter = (value: string, countryCode: CountryCode): string => {
    if (!value) {
        // cant return null, breaks WithFormatter => always return empty string instead
        return "";
    }

    if (countryCode !== CountryCode.CzechRepublic) {
        return value;
    }

    // add 0 prefix in case postal code is stored without leading zero
    const postalCode = value?.length === 4 ? `0${value}` : value;

    return `${postalCode.slice(0, 3)} ${postalCode.slice(3)}`;
};

export const hasOptionalPostalCode = (args: IGetValueArgs): boolean => {
    const address = args.storage.getValue(args.bindingContext.getParent()) as IAddressEntity;

    return isPostalCodeOptionalForCountry(address?.Country?.Code as CountryCode);
};

export const isPostalCodeOptionalForCountry = (countryCode: CountryCode): boolean => {
    return ![CountryCode.CzechRepublic, CountryCode.Slovakia].includes(countryCode);
};

export const czPostalCodeValidator = (value: TValue): boolean => {
    return !value || POSTAL_CODE_REG.test(removeWhiteSpace(value as string));
};

const postalCodeValidator = (value: TValue, args: IGetValueArgs): boolean => {
    if (hasOptionalPostalCode(args)) {
        return true;
    }

    return czPostalCodeValidator(value);
};

export const CZPostalCodePlaceholder = "123 45";

export const getPostalCodeDef: (isCZOnly?: boolean) => TFieldDefinition = (isCZOnly) => ({
    width: BasicInputSizes.S,
    validator: {
        type: ValidatorType.Custom,
        settings: {
            customValidator: isCZOnly ? czPostalCodeValidator : postalCodeValidator,
            message: i18next.t("Common:Validation.PostalCode")
        }
    },
    isRequired: isCZOnly ? true : not(hasOptionalPostalCode),
    formatter: (val, args) => {
        const address = args.storage.getValue(args.bindingContext.getParent()) as IAddressEntity;

        return postalCodeFormatter(val as string, address?.Country?.Code as CountryCode);
    },
    parser: (val) => {
        return removeWhiteSpace(val);
    },
    isValid: isCZOnly ? czPostalCodeValidator : postalCodeValidator,
    fieldSettings: {
        placeholder: (args) => {
            const address = args.storage.getValue(args.bindingContext.getParent()) as IAddressEntity;

            return !address?.Country?.Code || address.Country.Code === CountryCode.CzechRepublic ? CZPostalCodePlaceholder : "";
        }
    }
});

export const withDisplayName = <T extends IFieldInfoProperties | TFilterDef>(id: string, displayNameOrDef?: string | T, fieldDef?: T): Record<string, T extends IFieldDef ? IFieldDef : TFilterDef & {
    id: string
}> => {
    let displayName = "Name";
    if (typeof displayNameOrDef === "string") {
        displayName = displayNameOrDef;
    } else if (isNotDefined(fieldDef)) {
        fieldDef = displayNameOrDef;
    }
    return {
        [id]: {
            id,
            ...fieldDef,
            fieldSettings: {
                displayName,
                ...fieldDef?.fieldSettings
            }
        }
    };
};

/** Columns that should be available in almost all tables */
export const getCommonTableColumnsDefs = (): TFieldsDefinition => {
    return {
        DateCreated: {
            label: i18next.t("Common:General.Created")
        },
        DateLastModified: {},
        ...withDisplayName("CreatedBy"),
        ...withDisplayName("LastModifiedBy")
    };
};

/** Filters that should be available in almost all tables */
export const getCommonFilterDefs = (def?: TFilterDef): Record<string, TFilterDef> => ({
    DateCreated: { ...def },
    DateLastModified: { ...def },
    ...withDisplayName("CreatedBy", { isValueHelp: true, ...def }),
    ...withDisplayName("LastModifiedBy", { isValueHelp: true, ...def })
});

export const AttachmentsDef: IFilesDefinition = {
    collection: "Attachments",
    property: "File"
};

export function getFileMetadataAdditionalProps(property: string): IFieldDef[] {
    return [
        { id: "Name" },
        { id: "Size" },
        { id: "DateCreated" },
        { id: "DateLastModified" },
        { id: "IsIsdocReadable" },
        { id: "IsRossumReadable" },
    ].map(def => ({ ...def, id: `${property}/${def.id}` }));
}

export function getAttachmentAdditionalProps(path: string): IFieldDef[] {
    return [{
        id: path,
        additionalProperties: [
            ...getFileMetadataAdditionalProps("File"),
            { id: "Description" }
        ]
    }];
}

export const AttachmentsAdditionalProperties: IFieldDef[] = getAttachmentAdditionalProps("Attachments");
export function getReadOnlyAttachmentAdditionalProps(paths: string[]): IFieldDef[] {
    return paths.map(path => getAttachmentAdditionalProps(path)).flatMap(r => r);
}

export const getAttachmentsFilterDef = () => {
    return {
        type: FieldType.MultiSelect,
        valueType: ValueType.Boolean,
        isValueHelp: false,
        label: i18next.t("Common:General.Attachments"),
        fieldSettings: {
            items: getSimpleBoolSelectItems()
        },
        filter: {
            buildFilter(item: IChangedFilter, settings: ICreateFilterStringSettings): string | IFilterQuery {
                const values = item.value as boolean[];
                const hasTrue = values.some(v => v);
                const hasFalse = values.some(v => !v);

                if (hasTrue === hasFalse) {
                    return "";
                }

                if (hasTrue) {
                    return `Attachments/any()`;
                }

                if (hasFalse) {
                    return `not(Attachments/any())`;
                }

                return "";
            }
        }
    };
};

type THandleAttachmentClick = (storage: Model, entity: IEntity, entityType: EntityTypeName) => ((e: React.SyntheticEvent) => void);
export const getHandleAttachmentClick: THandleAttachmentClick = (storage, entity, entityType) => {
    return (e) => {
        e.stopPropagation();
        const id = entity[storage.data.bindingContext.getKeyPropertyName()];
        const url = `${getRouteByEntityType(entityType)}/${id}`;

        const origQueryParams = getQueryParameters();

        storage.history.push(url);

        setQueryParams({
            history: storage.history,
            newParams: {
                ...origQueryParams,
                [QueryParam.PaneStatus]: [PaneStatus.Normal, PaneStatus.Collapsed, PaneStatus.Normal].join("")
            }
        });
    };
};

export const getAttachmentsColumnDef = (entityType: EntityTypeName): TFieldDefinition => {
    return {
        textAlign: TextAlign.Center,
        formatter: (val: TValue, formatArgs: IFormatOptions) => {
            const { entity } = formatArgs;
            const hasAttachment = entity.Attachments?.length > 0;

            if (!hasAttachment) {
                return null;
            }

            const tooltip = i18next.t(`Common:General.WithAttachment`);
            return {
                value: (
                    <Clickable onClick={getHandleAttachmentClick(formatArgs.storage, entity, entityType)}>
                        <AttachmentIndicatorIcon title={tooltip}
                                                 isLightHover
                                                 width={IconSize.XL}
                                                 height={IconSize.M}/>
                    </Clickable>
                ),
                tooltip: tooltip
            };
        }
    };
};

export const getLinks = (args: IFormatOptions, items: TRecordAny[]): TCellValue => {
    let text = "";
    let fragment: React.ReactElement;
    const filteredItems = items?.filter(item => !!item.LinkedDocument?.NumberOurs);
    if (filteredItems?.length > 0) {
        fragment =
            <>
                {filteredItems.map((postingItem: IPaymentDocumentItemEntity, i: number) => {
                    if (postingItem.LinkedDocument?.Id) {
                        const route = getRouteByDocumentType(postingItem.LinkedDocument.DocumentTypeCode as DocumentTypeCode);
                        const to = `${route}/${postingItem.LinkedDocument.Id}`;
                        const intentLink = getIntentLink(postingItem.LinkedDocument.NumberOurs, {
                            route: to,
                            context: args.storage.context,
                            storage: args.storage
                        });
                        const isLast = filteredItems.length - 1 === i;
                        text = postingItem.LinkedDocument.NumberOurs;
                        if (!isLast) {
                            text += " ,";
                        }

                        return (
                            <React.Fragment key={postingItem.LinkedDocument?.Id}>
                                {intentLink}
                                {isLast ? "" : " ,"}
                            </React.Fragment>
                        );
                    }

                    return null;
                })}
            </>;
    }

    return {
        value: fragment ?? "",
        tooltip: text
    };
};

export const getBasePairedDocumentFormFieldDef = (): IFieldInfoProperties => ({
    // use comboBox with generated items with link prop, so we have correct field with clickable token in disabled state
    type: FieldType.ComboBox,
    label: i18next.t("Document:FormTab.PairedDocument"),
    fieldSettings: {
        displayName: DocumentEntity.NumberOurs,
        inputIcon: null,
        itemsFactory: async (args) => {
            const { storage, bindingContext } = args;
            const value = storage.getValue(bindingContext);
            const itemArr: IDocumentEntity[] = isObjectEmpty(value) ? null : Array.isArray(value) ? value : [value];
            return itemArr?.map((item): ISelectItem => {
                const route = getRouteByDocumentType(item.DocumentTypeCode);
                const linkParams = getIntentNavParams({
                    route: `${route}/${item.Id}`,
                    context: storage.context,
                    storage
                });

                return {
                    id: item.Id,
                    label: item.NumberOurs,
                    link: linkParams
                };
            });
        }
    },
    isRequired: false,
    isReadOnly: true,
});


const balanceSheetLayoutAccountValidator = (value: TValue, args: IGetValueArgs) => {
    const pattern = /^\d+$/;
    const isNumber = pattern.test(value as string);

    if (!value) {
        return new ValidationError(i18next.t("Banks:Validation.Required"), false, args.bindingContext.getPath(true));
    }

    if (!isNumber) {
        return new ValidationError(i18next.t("Banks:Validation.Balance"), false, args.bindingContext.getPath(true));
    }

    return true;
};

/** Custom data related to payment entities (CashBox, BankAccount) */
export interface IPaymentRelatedEntityCustomData extends IFormStorageDefaultCustomData {
    /** Usage data for the selected account in current CoA*/
    accountUsage?: IAccountUsage;
    // todo used in multiple places for isReadOnly check,
    // could be removed, because backend sends correct value in disabledProperties metadata, but field would change from readOnly to disabled
    /* we need to be able to access all bank accounts in AccountsRelatedToBankAccountsSelectPath definition
       so that we can filter out accounts already used in some bank account
       => store in PARENT_DATA_KEY custom data */
    parentData?: TEntityWithAccount[];
}

export function isBalanceSheetLayoutAccountUsed({ storage }: IGetValueArgs): boolean {
    const usage = (storage as FormStorage<unknown, IPaymentRelatedEntityCustomData>).getCustomData().accountUsage;

    return !storage.data.bindingContext.isNew() && isAccountUsedCheck(usage);
}

export async function balanceSheetLayoutAccountOnAfterLoad(storage: FormStorage, accPrefix?: string): Promise<void> {
    let usage: IAccountUsage = null;

    const bankAccount = storage.data.entity as ICompanyBankAccountEntity;
    if (!storage.data.bindingContext.isNew() && bankAccount?.BalanceSheetAccountNumberSuffix) {
        const accountNumber = `${accPrefix ?? bankAccount.BalanceSheetAccountNumberPrefix}${bankAccount.BalanceSheetAccountNumberSuffix}`;
        usage = await getAccountUsageAcrossCharts(accountNumber);
    }

    (storage as FormStorage<unknown, IPaymentRelatedEntityCustomData>).setCustomData({ accountUsage: usage });
}

export const AccountSettingsAccountsSelectPath = BindingContext.localContext("AccountSettingsAccountsSelect");
export const getBalanceSheetLayoutAccount = (accNumber: string[]): TFieldDefinition => {
    return {
        label: i18next.t("Document:Form.AnalyticalAccount"),
        width: BasicInputSizes.S,
        isRequired: true,
        validator: {
            type: ValidatorType.Custom,
            settings: {
                customValidator: balanceSheetLayoutAccountValidator
            }
        },
        extraFieldContent: accNumber?.length === 1 ? (args) => {
            const { bindingContext, storage, info } = args;
            const isReadOnly = isFieldReadOnly(info, storage, bindingContext);
            const styles = isReadOnly ? {} : { paddingLeft: "12px" };
            const groupStatus = (args.storage as FormStorage).getGroupStatus(getCollapsedGroupId({ id: AccountSettingsAccountsSelectPath }));
            return (
                <Text isLight={!groupStatus?.isCreating} style={styles}>
                    {accNumber[0]}
                </Text>);
        } : undefined
    };
};

export const fourDigitsFormatter = (val: TValue): string => {
    return NumberType.format(val as number, {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4
    });
};

export const ExchangeRatePerUnit = {
    defaultValue: (args: IGetValueArgs): number => {
        const document = args.storage.getEntity<IDocumentEntity>();
        return document.TransactionCurrencyCode === getCompanyCurrency(args.storage.context) ? 1 : null;
    },
    textAlign: TextAlign.Right,
    validator: {
        type: ValidatorType.Number,
        settings: {
            min: 0,
            excludeMin: true
        }
    },
    formatter(val: TValue, args: IFormatOptions): string {
        return fourDigitsFormatter(val);
    },
    isVisible: (args: IGetValueArgs): boolean => {
        const code = args.storage.data?.entity?.TransactionCurrency?.Code;
        return !!(code && code !== getCompanyCurrency(args.storage.context));
    }
};


export const NoteFieldDef = {
    isConfirmable: true,
    type: FieldType.EditableText,
    labelStatus: LabelStatus.Removed,
    isFullRow: true,
    fieldSettings: {
        isResponsive: true
    }
};

export const BirthNumberFormatter: TFormatterFn = (val: TValue, args?: IFormatOptions) => {
    const birthNumber = val as string;

    if (!birthNumber || birthNumber.length < 9) {
        return birthNumber;
    }

    const index = 6;

    return birthNumber.substring(0, index) + "/" + birthNumber.substring(index);
};

export function isBirthNumberValid(birthNumber: string): boolean {
    if (!birthNumber || birthNumber.length < 9) {
        return false;
    }
    birthNumber = birthNumber.replace("/", "");

    // split first part to year, month, day
    const year = parseInt(birthNumber.substring(0, 2), 10);
    const currentYear = getUtcDayjs().year() % 100;
    const fullYear = birthNumber.length === 9 || year > currentYear ? 1900 + year : 2000 + year;
    let month = parseInt(birthNumber.substring(2, 4), 10);
    const day = parseInt(birthNumber.substring(4, 6), 10);

    if (month >= 51) {
        month -= 50; // adjust for female birth numbers
    }
    // check if date is valid
    const date = getUtcDateBy(fullYear, month - 1, day);
    if (!isValidDate(date)
        || date.getUTCFullYear() !== fullYear
        || date.getUTCMonth() !== month - 1
        || date.getUTCDate() !== day) {
        return false;
    }

    if (fullYear < 1954) {
        // no modulo validation for birth numbers before 1954
        return true;
    }

    // Birth numbers after 1954 must be 10 digits long
    if (birthNumber.length !== 10) {
        return false;
    }

    const number = parseInt(birthNumber.substring(0, 9), 10);
    const lastDigit = parseInt(birthNumber.substring(9, 10), 10);

    const div11remain = number % 11;

    return div11remain === lastDigit || (fullYear < 1985 && div11remain === 10 && lastDigit === 0);
}

export const BirthNumberFieldDef: TFieldDefinition = {
    // do we want to implement birth number check function?
    width: BasicInputSizes.M,
    // isValid
    parser: (val: string, args?: IFormatOptions) => {
        return val.replace("/", "");
    },
    formatter: BirthNumberFormatter,
    validator: {
        type: ValidatorType.Custom,
        settings: {
            customValidator: (value: TValue, args: IGetValueArgs): TValidatorFnResult => {
                const birthNumber = value as string;

                if (!birthNumber) {
                    return true;
                }

                if (!isBirthNumberValid(birthNumber)) {
                    return new ValidationError(args.storage.t("Common:Validation.BirthNumber"), false, args.bindingContext.getEntityPath(false));
                }

                return true;
            }
        }
    }
};

export function emailFormatter(value: TValue, opts?: IFormatOptions): ICellValueObject {
    const { name } = opts?.customArgs ?? {};
    return value ? {
        tooltip: `${value}`,
        value: (
            <Clickable link={`mailto:${value}`} isLink><strong>{name ?? value}</strong></Clickable>
        )
    } : null;
}

/**
 * form-based isReadOnly flag - system entities (with negative IDs => id < 0) are readOnly
 */
export const isSystemEntityId = (id: TId): boolean => (id as number) < 0;
export const isSystemEntity = (args: IGetValueArgs): boolean => isSystemEntityId(args.data?.Id);